import {
	createContext,
	PropsWithChildren,
	useContext,
	FC,
	useRef,
	useEffect,
	useCallback,
	RefObject
} from 'react';
import { getUserDataToWriteIntoAnalytics, UserDto } from '@sweatpals/shared';
import { getApps, initializeApp } from 'firebase/app';
import { getAnalytics, isSupported } from 'firebase/analytics';
import { setUserId, setUserProperties } from '@firebase/analytics';

export interface FirebaseContextShape {
	isInitializedRef?: RefObject<boolean>;
	isAnalyticsSupportedRef?: RefObject<boolean>;
	setUserIdExternally?: (userId: string) => void;
}

const FirebaseContext = createContext<FirebaseContextShape>({});

export const useFirebaseContext = () => useContext(FirebaseContext);

export type FirebaseContextProviderProps = PropsWithChildren & {
	currentUser?: UserDto | null;
	currentCommunity?: UserDto | null;
}

/**
 * Firebase context provider for handling analytics and feature flags in the application.
 *
 * The main purpose of this context is to make logging and feature-flags versatile for any part of the app.
 * It provides functionality for initializing Firebase, setting user IDs for analytics, and handling feature flags.
 *
 * @example
 * ```tsx
 * import { FirebaseContextProvider } from './firebase.context';
 *
 * function App() {
 *   const user = getCurrentUser();
 *   const community = getCurrentCommunity();
 *
 *   return (
 *     <FirebaseContextProvider currentUser={user} currentCommunity={community}>
 *       <YourAppContent />
 *     </FirebaseContextProvider>
 *   );
 * }
 * ```
 *
 * @note
 * Note: This context enables the use of hook utilities like use-firebase-analytics
 * anywhere in your component tree of any app. These hooks can be
 * used in any component that's wrapped with this provider.
 */
export const FirebaseContextProvider: FC<FirebaseContextProviderProps> = ({ children, currentUser, currentCommunity }) => {
	const isInitializedRef = useRef<boolean>(false);
	const isAnalyticsSupportedRef = useRef<boolean>(false);

	useEffect(() => {
		if (typeof window !== undefined && !isInitializedRef.current) {
			const firebaseConfig = {
				apiKey: process.env['NEXT_PUBLIC_FIREBASE_API_KEY'] ?? process.env['NX_PUBLIC_FIREBASE_API_KEY'],
				authDomain: process.env['NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN'] ?? process.env['NX_PUBLIC_FIREBASE_AUTH_DOMAIN'],
				projectId: process.env['NEXT_PUBLIC_FIREBASE_PROJECT_ID'] ?? process.env['NX_PUBLIC_FIREBASE_PROJECT_ID'],
				storageBucket: process.env['NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET'] ?? process.env['NX_PUBLIC_FIREBASE_STORAGE_BUCKET'],
				messagingSenderId: process.env['NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID'] ?? process.env['NX_PUBLIC_FIREBASE_MESSAGING_SENDER_ID'],
				appId: process.env['NEXT_PUBLIC_FIREBASE_APP_ID'] ?? process.env['NX_PUBLIC_FIREBASE_APP_ID'],
				measurementId: process.env['NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID'] ?? process.env['NX_PUBLIC_FIREBASE_MEASUREMENT_ID'],
			};

			if (!getApps().length) {
				const app = initializeApp(firebaseConfig);
				getAnalytics(app);
			}

			isSupported()
				.then(supported => {
					console.info('[Firebase] is supported: ', supported);
					isAnalyticsSupportedRef.current = supported;
				});
		}
	}, [isInitializedRef.current, isAnalyticsSupportedRef.current]);

	const setCurrentUser = useCallback((currentUser?: UserDto) => {
		try {
			const analytics = getAnalytics(getApps()[0]);
			setUserId(analytics, currentUser?.id ?? null);
			const analyticsUser = getUserDataToWriteIntoAnalytics(currentUser);

			setUserProperties(analytics, analyticsUser);

			console.info('[Firebase] User id set: ', currentUser?.id);
		} catch (e) {
			console.error('[Firebase] Error while setting user id', e);
		}
	}, []);

	useEffect(() => {
		if (currentUser?.id && isAnalyticsSupportedRef.current) {
			setCurrentUser(currentUser);
		}
	}, [isAnalyticsSupportedRef.current, setCurrentUser, currentUser?.id, currentCommunity?.id]);

	const setUserIdExternally = useCallback((userId: string) => {
		try {
			const analytics = getAnalytics();
			setUserId(analytics, userId);

			console.info('[Firebase] User id set: ', userId);
		} catch (e) {
			console.error('[Firebase] Error while setting user id', e);
		}
	}, []);

	return (
		<FirebaseContext.Provider value={{ isInitializedRef, setUserIdExternally, isAnalyticsSupportedRef }}>
			{children}
		</FirebaseContext.Provider>
	);
};
